"use client";

import type {
  CandidateOption,
  CandidateSpan,
  ConvertFeature,
  DiagnosticTraceStep,
  Draft,
  EffectiveEntitlement,
  HistoryEntry,
  LanguagePreference,
  MobileSession,
  Session,
  SupportedLanguageCode,
  TrialHandoffSnapshot,
} from "@fluentry/domain";
import { Badge, Button, EmptyState, PanelCard } from "@fluentry/ui";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Clock,
  Command,
  Copy,
  Download,
  FileText,
  History,
  Loader2,
  Sparkles,
  Trash2,
  WandSparkles,
} from "lucide-react";
import {
  deleteDraft as deleteDraftRequest,
  deleteHistoryEntry as deleteHistoryEntryRequest,
  fetchDrafts,
  fetchEffectiveEntitlement,
  fetchHistory,
  requestAppConvert,
  saveDraft,
} from "./api";
import { ApiClientError } from "./http";
import { CandidateEditor } from "./candidate-editor";
import { CandidatePopover, type PopoverAnchorRect, useCoarsePointer } from "./candidate-popover";
import {
  buildResultExportText,
  ConvertResultSummary,
  downloadResultText,
  writeClipboardText,
} from "./convert-result-summary";
import { FeatureControls } from "./feature-controls";
import {
  applyCandidateReplacement,
  getActiveCandidateLayer,
  rebaseCandidateSpansForDocumentChange,
} from "./candidate-state";
import { LanguageControls } from "./language-controls";
import {
  canSwapLanguagePreferences,
  swapLanguagePreferences,
  toConvertRequestLanguages,
} from "./language-preferences";
import {
  convertResultToDocumentState,
  draftToDocumentState,
  formatHistoryTimestamp,
  getWorkbenchStatus,
  historyEntryToDocumentState,
  resolveWorkbenchSectionFromHash,
  shouldBlockDraftUnload,
  trialHandoffSnapshotToDocumentState,
  WORKBENCH_SECTION_IDS,
  type ActiveResultSource,
  type DraftUnloadSaveState,
} from "./workbench-core.helpers";

type WorkbenchCoreProps = {
  apiBaseUrl: string;
  session: Session | null;
  mobileSession?: MobileSession | null;
  freshDraftRequest?: boolean;
  trialHandoffSnapshot?: TrialHandoffSnapshot | null;
  onFreshDraftConsumed?: () => void;
  onTrialHandoffConsumed?: () => void;
  billingUrl?: string;
  renderBillingLink?: (props: {
    href: string;
    className?: string;
    children: ReactNode;
  }) => ReactNode;
};

type DraftSaveState = DraftUnloadSaveState;
type DraftDeleteState = {
  confirmingId: string | null;
  deletingId: string | null;
  surface: "archive" | "resume" | null;
  error: string | null;
};
type HistoryDeleteState = {
  confirmingId: string | null;
  deletingId: string | null;
  surface: "archive" | "resume" | null;
  error: string | null;
};

type ShareActionState = {
  key: string | null;
  action: "copy" | "download" | null;
  status: "idle" | "done" | "error";
};

type ResultExportPayload = Parameters<typeof buildResultExportText>[0];
// The real workbench opens on a blank canvas so the editor placeholder invites the user's own draft instead of a canned demo sentence.
const INITIAL_DRAFT = "";
// Fresh-session preference defaults. These intentionally mirror the domain schema defaults
// (DraftSchema / HistoryEntrySchema) so a brand-new workbench and a freshly persisted record
// agree on the same starting pair instead of duplicating magic literals across reset paths.
const INITIAL_FEATURE: ConvertFeature = "interlingua";
const INITIAL_NATIVE_LANGUAGE: LanguagePreference = "ja";
const INITIAL_TARGET_LANGUAGE: LanguagePreference = "auto";
type ResolvedLanguagePair = {
  nativeLanguage: SupportedLanguageCode;
  targetLanguage: SupportedLanguageCode;
};
type WorkbenchStarterDraft = {
  id: string;
  label: string;
  feature: ConvertFeature;
  text: string;
  nativeLanguage: LanguagePreference;
  targetLanguage: LanguagePreference;
};

const WORKBENCH_STARTER_DRAFTS: WorkbenchStarterDraft[] = [
  {
    id: "mixed-language",
    label: "Mixed-language request",
    feature: "interlingua",
    text: "I need to 予約を変更したい because my flight got delayed.",
    nativeLanguage: "ja",
    targetLanguage: "en",
  },
  {
    id: "tone-rewrite",
    label: "Tone rewrite",
    feature: "phrasing",
    text: "Can you make this sound less pushy: send me the file today.",
    nativeLanguage: "en",
    targetLanguage: "en",
  },
  {
    id: "sound-guess",
    label: "Sound guess",
    feature: "phonology",
    text: "I heard komfutaburu in class and need the real English word.",
    nativeLanguage: "ja",
    targetLanguage: "en",
  },
];

const WORKBENCH_SECTION_DOM_IDS = {
  drafts: `workbench-section-${WORKBENCH_SECTION_IDS.drafts}`,
  history: `workbench-section-${WORKBENCH_SECTION_IDS.history}`,
} as const;

export const WORKBENCH_DRAFT_FLUSH_EVENT = "fluentry:workbench-draft-flush";

function countActiveCandidateSpans(candidateState: CandidateSpan[]) {
  // Count active spans without allocating a filtered array because this runs on every editor render.
  let count = 0;
  for (const span of candidateState) {
    if (span.status === "active") {
      count += 1;
    }
  }
  return count;
}

function summarizeDraftAction(entry: Draft) {
  if (entry.targetOutput) {
    return "Result ready";
  }
  if (entry.alternatives.some((span) => span.status === "applied")) {
    return "Edits reviewed";
  }
  if (entry.alternatives.length > 0) {
    return "Needs review";
  }
  return "Draft only";
}

function getDraftPreview(entry: Draft) {
  return entry.targetOutput ?? entry.interpretedIntent ?? entry.sourceInput;
}

function draftSharePayload(entry: Draft) {
  if (!entry.targetOutput) {
    return null;
  }

  return {
    sourceInput: entry.sourceInput,
    interpretedIntent: entry.interpretedIntent,
    targetOutput: entry.targetOutput,
    diagnosticTrace: entry.diagnosticTrace,
    resolvedTargetLanguage: entry.resolvedTargetLanguage,
  };
}

function historySharePayload(entry: HistoryEntry) {
  return {
    sourceInput: entry.sourceInput,
    interpretedIntent: entry.interpretedIntent,
    targetOutput: entry.targetOutput,
    diagnosticTrace: entry.diagnosticTrace,
    resolvedTargetLanguage: entry.resolvedTargetLanguage,
  };
}

function areCandidateOptionsEqual(left: CandidateOption[], right: CandidateOption[]) {
  if (left === right) {
    return true;
  }

  if (left.length !== right.length) {
    return false;
  }

  for (let index = 0; index < left.length; index += 1) {
    const leftOption = left[index];
    const rightOption = right[index];
    if (
      leftOption.id !== rightOption.id ||
      leftOption.text !== rightOption.text ||
      leftOption.score !== rightOption.score ||
      leftOption.recommended !== rightOption.recommended ||
      leftOption.reason !== rightOption.reason
    ) {
      return false;
    }
  }

  return true;
}

function areCandidateSpansEqual(left: CandidateSpan[], right: CandidateSpan[]) {
  if (left === right) {
    return true;
  }

  if (left.length !== right.length) {
    return false;
  }

  for (let index = 0; index < left.length; index += 1) {
    const leftSpan = left[index];
    const rightSpan = right[index];
    if (
      leftSpan.id !== rightSpan.id ||
      leftSpan.start !== rightSpan.start ||
      leftSpan.end !== rightSpan.end ||
      leftSpan.sourceText !== rightSpan.sourceText ||
      leftSpan.scope !== rightSpan.scope ||
      leftSpan.explanation !== rightSpan.explanation ||
      leftSpan.status !== rightSpan.status ||
      leftSpan.appliedCandidateId !== rightSpan.appliedCandidateId ||
      !areCandidateOptionsEqual(leftSpan.candidates, rightSpan.candidates)
    ) {
      return false;
    }
  }

  return true;
}

function areDiagnosticTraceStepsEqual(
  left: DiagnosticTraceStep[] | undefined,
  right: DiagnosticTraceStep[] | undefined,
) {
  if (left === right) {
    return true;
  }

  if (!left || !right) {
    return left === right;
  }

  if (left.length !== right.length) {
    return false;
  }

  for (let index = 0; index < left.length; index += 1) {
    const leftStep = left[index];
    const rightStep = right[index];
    if (leftStep.label !== rightStep.label || leftStep.detail !== rightStep.detail) {
      return false;
    }
  }

  return true;
}

function appendBillingSource(href: string, source: "quota") {
  const [path, query = ""] = href.split("?", 2);
  const params = new URLSearchParams(query);
  params.set("source", source);
  return `${path}?${params.toString()}`;
}

function matchesActiveDraftSnapshot(args: {
  activeDraft: Draft | null;
  document: string;
  activeFeature: ConvertFeature;
  candidateState: CandidateSpan[];
  targetOutput: string | undefined;
  interpretedIntent: string | undefined;
  diagnosticTrace: DiagnosticTraceStep[] | undefined;
  analysisExplanation: string | undefined;
  nativeLanguagePreference: LanguagePreference;
  targetLanguagePreference: LanguagePreference;
  followInputLanguage: boolean;
  lastResolvedLanguagePair: ResolvedLanguagePair | null;
}) {
  const {
    activeDraft,
    document,
    activeFeature,
    candidateState,
    targetOutput,
    interpretedIntent,
    diagnosticTrace,
    analysisExplanation,
    nativeLanguagePreference,
    targetLanguagePreference,
    followInputLanguage,
    lastResolvedLanguagePair,
  } = args;

  // Compare the live draft structurally so autosave doesn't allocate large JSON snapshots on every keystroke.
  return (
    activeDraft?.sourceInput === document &&
    activeDraft.feature === activeFeature &&
    areCandidateSpansEqual(activeDraft.alternatives, candidateState) &&
    activeDraft.targetOutput === targetOutput &&
    activeDraft.interpretedIntent === interpretedIntent &&
    areDiagnosticTraceStepsEqual(activeDraft.diagnosticTrace, diagnosticTrace) &&
    activeDraft.explanation === analysisExplanation &&
    activeDraft.nativeLanguage === nativeLanguagePreference &&
    activeDraft.targetLanguage === targetLanguagePreference &&
    activeDraft.followInputLanguage === followInputLanguage &&
    activeDraft.resolvedNativeLanguage === lastResolvedLanguagePair?.nativeLanguage &&
    activeDraft.resolvedTargetLanguage === lastResolvedLanguagePair?.targetLanguage
  );
}

export function WorkbenchCore({
  apiBaseUrl,
  session,
  mobileSession = null,
  freshDraftRequest = false,
  trialHandoffSnapshot = null,
  onFreshDraftConsumed,
  onTrialHandoffConsumed,
  billingUrl = "/settings/billing",
  renderBillingLink,
}: WorkbenchCoreProps) {
  const [document, setDocument] = useState(INITIAL_DRAFT);
  const [activeFeature, setActiveFeature] = useState<ConvertFeature>(INITIAL_FEATURE);
  const [candidateState, setCandidateState] = useState<CandidateSpan[]>([]);
  const [editorTouched, setEditorTouched] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [activeHistoryEntryId, setActiveHistoryEntryId] = useState<string | null>(null);
  const [activeDraftId, setActiveDraftId] = useState<string | null>(null);
  const [activeResultSource, setActiveResultSource] = useState<ActiveResultSource | null>(null);
  const [draftSaveState, setDraftSaveState] = useState<DraftSaveState>("idle");
  const [draftSaveError, setDraftSaveError] = useState<string | null>(null);
  const [draftLoadError, setDraftLoadError] = useState<string | null>(null);
  const [historyLoadError, setHistoryLoadError] = useState<string | null>(null);
  const [draftDeleteState, setDraftDeleteState] = useState<DraftDeleteState>({
    confirmingId: null,
    deletingId: null,
    surface: null,
    error: null,
  });
  const [historyDeleteState, setHistoryDeleteState] = useState<HistoryDeleteState>({
    confirmingId: null,
    deletingId: null,
    surface: null,
    error: null,
  });
  const [shareActionState, setShareActionState] = useState<ShareActionState>({
    key: null,
    action: null,
    status: "idle",
  });
  const [entitlement, setEntitlement] = useState<EffectiveEntitlement | null>(null);
  const [entitlementLoading, setEntitlementLoading] = useState(false);
  const [entitlementError, setEntitlementError] = useState<string | null>(null);
  const [selectionRange, setSelectionRange] = useState<{
    start: number;
    end: number;
  } | null>(null);
  const [activeSpanId, setActiveSpanId] = useState<string | null>(null);
  const [hoveredSpanId, setHoveredSpanId] = useState<string | null>(null);
  const [popoverAnchorRect, setPopoverAnchorRect] = useState<PopoverAnchorRect | null>(null);
  const [popoverPinned, setPopoverPinned] = useState(false);
  const [popoverHovered, setPopoverHovered] = useState(false);
  const coarsePointer = useCoarsePointer();
  const [confidence, setConfidence] = useState<number | undefined>(undefined);
  const [analysisExplanation, setAnalysisExplanation] = useState<string | undefined>(undefined);
  const [targetOutput, setTargetOutput] = useState<string | undefined>(undefined);
  const [interpretedIntent, setInterpretedIntent] = useState<string | undefined>(undefined);
  const [diagnosticTrace, setDiagnosticTrace] = useState<DiagnosticTraceStep[] | undefined>(
    undefined,
  );
  const [nativeLanguagePreference, setNativeLanguagePreference] =
    useState<LanguagePreference>(INITIAL_NATIVE_LANGUAGE);
  const [targetLanguagePreference, setTargetLanguagePreference] =
    useState<LanguagePreference>(INITIAL_TARGET_LANGUAGE);
  const [followInputLanguage, setFollowInputLanguage] = useState(false);
  const [lastResolvedLanguagePair, setLastResolvedLanguagePair] =
    useState<ResolvedLanguagePair | null>(null);
  const [lastAssistRequestDocument, setLastAssistRequestDocument] = useState<string | null>(null);
  const [draftsHydrated, setDraftsHydrated] = useState(false);
  const documentRef = useRef(document);
  const hidePreviewTimeoutRef = useRef<number | null>(null);
  const freshDraftAppliedRef = useRef(false);
  const freshDraftTransitionRef = useRef(false);
  const trialHandoffAppliedRef = useRef<string | null>(null);
  const saveDraftSequenceRef = useRef(0);
  const latestDraftSnapshotRef = useRef<Draft | null>(null);
  const latestUnsavedDraftRef = useRef(false);
  const latestPersistContextRef = useRef<{
    apiBaseUrl: string;
    mobileSession: MobileSession | null;
  }>({ apiBaseUrl, mobileSession });
  const convertRequestSequenceRef = useRef(0);
  // Read the selected draft directly so the Compiler can own this pure lookup while the save effect still depends on the real inputs.
  const activeDraft = drafts.find((entry) => entry.id === activeDraftId) ?? null;
  const draftMatchesCurrentSnapshot = useMemo(
    () =>
      matchesActiveDraftSnapshot({
        activeDraft,
        document,
        activeFeature,
        candidateState,
        targetOutput,
        interpretedIntent,
        diagnosticTrace,
        analysisExplanation,
        nativeLanguagePreference,
        targetLanguagePreference,
        followInputLanguage,
        lastResolvedLanguagePair,
      }),
    [
      activeDraft,
      activeFeature,
      analysisExplanation,
      candidateState,
      diagnosticTrace,
      document,
      followInputLanguage,
      interpretedIntent,
      lastResolvedLanguagePair,
      nativeLanguagePreference,
      targetLanguagePreference,
      targetOutput,
    ],
  );
  const shouldPersistActiveDraft = Boolean(
    session?.authenticated &&
    activeDraftId &&
    document.trim().length > 0 &&
    (activeDraft || editorTouched),
  );
  const hasUnsavedDraftChanges = shouldBlockDraftUnload({
    shouldPersistActiveDraft,
    draftSaveState,
    draftMatchesCurrentSnapshot,
  });
  latestDraftSnapshotRef.current = buildActiveDraftSnapshot();
  latestUnsavedDraftRef.current = hasUnsavedDraftChanges;
  latestPersistContextRef.current = { apiBaseUrl, mobileSession };
  const languageStateKey = `${nativeLanguagePreference}:${targetLanguagePreference}:${followInputLanguage}:${
    lastResolvedLanguagePair?.nativeLanguage ?? ""
  }:${lastResolvedLanguagePair?.targetLanguage ?? ""}`;
  // Hover should stay lightweight, while click pins the same suggestion thread in place until the user dismisses it.
  const displaySpanId = popoverPinned ? activeSpanId : (hoveredSpanId ?? activeSpanId);
  const activeLayer = useMemo(
    () => getActiveCandidateLayer(candidateState, selectionRange, displaySpanId),
    [candidateState, displaySpanId, selectionRange],
  );
  const selectedSpan = activeLayer.primarySpan;
  const status = useMemo(
    () =>
      getWorkbenchStatus({
        loading,
        error,
        candidateCount: countActiveCandidateSpans(candidateState),
        activeResultSource,
      }),
    [activeResultSource, candidateState, error, loading],
  );
  const effectivePlanCode = entitlement?.effectivePlanCode ?? "free";
  const planLabel =
    effectivePlanCode === "team"
      ? "Team plan"
      : effectivePlanCode === "pro"
        ? "Pro plan"
        : "Free plan";
  const planStatusLabel = entitlement
    ? `${planLabel} · ${entitlement.effectiveStatus}`
    : entitlementLoading
      ? "Checking plan..."
      : "Plan status unavailable";
  const isPaidPlan = effectivePlanCode === "pro" || effectivePlanCode === "team";
  const savedRunUsage = entitlement?.usage.savedRuns ?? null;
  const savedRunQuotaLabel = savedRunUsage
    ? savedRunUsage.limit == null
      ? `${savedRunUsage.used} / Unlimited`
      : `${savedRunUsage.used} / ${savedRunUsage.limit}`
    : `${history.length} saved`;
  const savedRunQuotaDetail = savedRunUsage
    ? savedRunUsage.limit == null
      ? "Unlimited saved workbench runs"
      : `${savedRunUsage.remaining} saved runs left this month`
    : "History entries ready to replay";
  const savedRunQuotaLimitReached = savedRunUsage?.limitReached === true;
  const quotaExceededError =
    errorCode === "QUOTA_EXCEEDED" || error?.includes("Monthly saved-run quota") === true;
  const showQuotaRecovery = savedRunQuotaLimitReached || quotaExceededError;
  const quotaBillingUrl = appendBillingSource(billingUrl, "quota");
  const billingCtaLabel = isPaidPlan
    ? "Manage billing"
    : showQuotaRecovery
      ? "Review billing"
      : "Compare plans";

  documentRef.current = document;

  function clearWorkbenchError() {
    setError(null);
    setErrorCode(null);
  }

  function setWorkbenchError(message: string, code?: string | null) {
    setError(message);
    setErrorCode(code ?? null);
  }

  function markDraftDirty(nextDocument = documentRef.current) {
    if (!session?.authenticated || !activeDraftId) {
      return;
    }

    setDraftSaveState(nextDocument.trim().length === 0 ? "idle" : "dirty");
    setDraftSaveError(null);
  }

  function buildActiveDraftSnapshot(): Draft | null {
    if (!activeDraftId || document.trim().length === 0) {
      return null;
    }

    return {
      id: activeDraftId,
      feature: activeFeature,
      sourceInput: document,
      interpretedIntent,
      targetOutput,
      nativeLanguage: nativeLanguagePreference,
      targetLanguage: targetLanguagePreference,
      followInputLanguage,
      alternatives: candidateState,
      diagnosticTrace,
      explanation: analysisExplanation,
      resolvedNativeLanguage: lastResolvedLanguagePair?.nativeLanguage,
      resolvedTargetLanguage: lastResolvedLanguagePair?.targetLanguage,
      updatedAt: activeDraft?.updatedAt ?? new Date().toISOString(),
    };
  }

  async function persistActiveDraftSnapshot() {
    if (!session?.authenticated) {
      return false;
    }

    const draftSnapshot = buildActiveDraftSnapshot();
    if (!draftSnapshot) {
      setDraftSaveState("idle");
      setDraftSaveError(null);
      setDraftLoadError(null);
      setHistoryLoadError(null);
      return true;
    }

    setDraftSaveState("saving");
    setDraftSaveError(null);
    const currentSaveSequence = ++saveDraftSequenceRef.current;

    try {
      const savedDraft = await saveDraft(apiBaseUrl, draftSnapshot.id, draftSnapshot, {
        mobileSession,
      });

      if (currentSaveSequence !== saveDraftSequenceRef.current) {
        return false;
      }

      setDrafts((currentDrafts) => {
        const hasDraft = currentDrafts.some((entry) => entry.id === savedDraft.id);
        if (!hasDraft) {
          return [savedDraft, ...currentDrafts];
        }

        return currentDrafts
          .map((entry) => (entry.id === savedDraft.id ? savedDraft : entry))
          .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
      });
      setDraftSaveState("saved");
      setDraftSaveError(null);
      return true;
    } catch (saveError) {
      if (currentSaveSequence !== saveDraftSequenceRef.current) {
        return false;
      }

      setDraftSaveState("error");
      setDraftSaveError(saveError instanceof Error ? saveError.message : "Draft save failed");
      return false;
    }
  }

  function renderBillingCta(className: string | undefined, children: ReactNode, href = billingUrl) {
    // Product shells can provide router-aware navigation while shared previews and public embeds keep a plain anchor.
    if (renderBillingLink) {
      return renderBillingLink({ href, className, children });
    }

    return (
      <a href={href} className={className}>
        {children}
      </a>
    );
  }

  useEffect(() => {
    if (!freshDraftRequest) {
      freshDraftAppliedRef.current = false;
    }
  }, [freshDraftRequest]);

  useEffect(() => {
    if (!trialHandoffSnapshot) {
      trialHandoffAppliedRef.current = null;
    }
  }, [trialHandoffSnapshot]);

  useEffect(() => {
    return () => {
      if (hidePreviewTimeoutRef.current != null) {
        window.clearTimeout(hidePreviewTimeoutRef.current);
      }
    };
  }, [persistActiveDraftSnapshot]);

  useEffect(() => {
    if (!popoverPinned && !hoveredSpanId) {
      return;
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key !== "Escape") {
        return;
      }

      clearSuggestionPopover();
    }

    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [hoveredSpanId, popoverPinned]);

  useEffect(() => {
    if (!session?.authenticated) {
      freshDraftAppliedRef.current = false;
      setHistory([]);
      setDrafts([]);
      setActiveDraftId(null);
      setDraftSaveState("idle");
      setDraftSaveError(null);
      setDraftDeleteState({
        confirmingId: null,
        deletingId: null,
        surface: null,
        error: null,
      });
      setHistoryDeleteState({
        confirmingId: null,
        deletingId: null,
        surface: null,
        error: null,
      });
      setEntitlement(null);
      setEntitlementLoading(false);
      setEntitlementError(null);
      setDocument(INITIAL_DRAFT);
      setActiveFeature(INITIAL_FEATURE);
      setCandidateState([]);
      setEditorTouched(false);
      setConfidence(undefined);
      setAnalysisExplanation(undefined);
      setTargetOutput(undefined);
      setInterpretedIntent(undefined);
      setDiagnosticTrace(undefined);
      setNativeLanguagePreference(INITIAL_NATIVE_LANGUAGE);
      setTargetLanguagePreference(INITIAL_TARGET_LANGUAGE);
      setFollowInputLanguage(false);
      setLastResolvedLanguagePair(null);
      setLastAssistRequestDocument(null);
      setDraftsHydrated(false);
      return;
    }

    setDraftsHydrated(false);
    setEntitlementLoading(true);
    setEntitlementError(null);
    setDraftLoadError(null);
    setHistoryLoadError(null);
    let cancelled = false;
    void Promise.allSettled([
      fetchHistory(apiBaseUrl, { mobileSession }),
      fetchDrafts(apiBaseUrl, { mobileSession }),
      fetchEffectiveEntitlement(apiBaseUrl, { mobileSession }),
    ])
      .then(([historyResult, draftsResult, entitlementResult]) => {
        if (cancelled) {
          return;
        }

        if (historyResult.status === "fulfilled") {
          setHistory(historyResult.value);
          setHistoryLoadError(null);
        } else {
          console.warn("Failed to fetch workbench history", historyResult.reason);
          setHistoryLoadError(
            historyResult.reason instanceof Error
              ? historyResult.reason.message
              : "Saved runs are unavailable",
          );
        }

        if (draftsResult.status === "fulfilled") {
          setDrafts(draftsResult.value);
          setDraftLoadError(null);
        } else {
          console.warn("Failed to fetch workbench drafts", draftsResult.reason);
          setDraftLoadError(
            draftsResult.reason instanceof Error
              ? draftsResult.reason.message
              : "Draft snapshots are unavailable",
          );
        }

        if (entitlementResult.status === "fulfilled") {
          setEntitlement(entitlementResult.value);
        } else {
          console.warn("Failed to fetch workbench entitlement", entitlementResult.reason);
          setEntitlementError(
            entitlementResult.reason instanceof Error
              ? entitlementResult.reason.message
              : "Plan status is unavailable",
          );
        }
      })
      .finally(() => {
        if (cancelled) {
          return;
        }

        setDraftsHydrated(true);
        setEntitlementLoading(false);
      });

    // React StrictMode and route changes can abandon the first load pass; stale failures should not overwrite the live workbench state.
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, mobileSession, session]);

  useEffect(() => {
    if (
      !freshDraftRequest ||
      !session?.authenticated ||
      !draftsHydrated ||
      trialHandoffSnapshot ||
      freshDraftAppliedRef.current ||
      freshDraftTransitionRef.current
    ) {
      return;
    }

    freshDraftTransitionRef.current = true;
    let cancelled = false;

    void (async () => {
      if (hasUnsavedDraftChanges) {
        const savedBeforeReset = await persistActiveDraftSnapshot();
        if (cancelled) {
          return;
        }

        if (!savedBeforeReset) {
          freshDraftTransitionRef.current = false;
          return;
        }
      }

      if (cancelled) {
        return;
      }

      // Fresh draft mode intentionally overrides the usual "resume the latest save" behavior only after the current draft is durable.
      freshDraftAppliedRef.current = true;
      freshDraftTransitionRef.current = false;
      setActiveDraftId(`draft_${crypto.randomUUID()}`);
      setActiveHistoryEntryId(null);
      setActiveResultSource(null);
      setDocument(INITIAL_DRAFT);
      setActiveFeature(INITIAL_FEATURE);
      setCandidateState([]);
      setEditorTouched(false);
      setConfidence(undefined);
      setAnalysisExplanation(undefined);
      setTargetOutput(undefined);
      setInterpretedIntent(undefined);
      setDiagnosticTrace(undefined);
      setNativeLanguagePreference(INITIAL_NATIVE_LANGUAGE);
      setTargetLanguagePreference(INITIAL_TARGET_LANGUAGE);
      setFollowInputLanguage(false);
      setLastResolvedLanguagePair(null);
      setLastAssistRequestDocument(null);
      setDraftSaveState("idle");
      setDraftSaveError(null);
      setDraftDeleteState({
        confirmingId: null,
        deletingId: null,
        surface: null,
        error: null,
      });
      setHistoryDeleteState({
        confirmingId: null,
        deletingId: null,
        surface: null,
        error: null,
      });
      clearSuggestionPopover();
      onFreshDraftConsumed?.();
    })();

    return () => {
      cancelled = true;
    };
  }, [draftsHydrated, freshDraftRequest, onFreshDraftConsumed, session, trialHandoffSnapshot]);

  useEffect(() => {
    if (!trialHandoffSnapshot || !session?.authenticated) {
      return;
    }

    const handoffKey = `${trialHandoffSnapshot.createdAt}:${trialHandoffSnapshot.sourceInput}:${trialHandoffSnapshot.feature}`;
    if (trialHandoffAppliedRef.current === handoffKey) {
      return;
    }

    // Trial handoff wins over normal draft restore once so signup lands on the value the visitor already created.
    trialHandoffAppliedRef.current = handoffKey;
    freshDraftAppliedRef.current = false;
    const restored = trialHandoffSnapshotToDocumentState(trialHandoffSnapshot);
    setActiveDraftId(`draft_trial_${crypto.randomUUID()}`);
    setActiveHistoryEntryId(null);
    setActiveResultSource({ kind: "trial-handoff" });
    setDocument(restored.document);
    setActiveFeature(restored.feature);
    setCandidateState(restored.candidateState);
    setEditorTouched(true);
    setConfidence(restored.confidence);
    setAnalysisExplanation(restored.explanation);
    setTargetOutput(restored.targetOutput);
    setInterpretedIntent(restored.interpretedIntent);
    setDiagnosticTrace(restored.diagnosticTrace);
    setNativeLanguagePreference(restored.nativeLanguagePreference);
    setTargetLanguagePreference(restored.targetLanguagePreference);
    setFollowInputLanguage(restored.followInputLanguage);
    setLastResolvedLanguagePair(
      restored.resolvedNativeLanguage && restored.resolvedTargetLanguage
        ? {
            nativeLanguage: restored.resolvedNativeLanguage,
            targetLanguage: restored.resolvedTargetLanguage,
          }
        : null,
    );
    setLastAssistRequestDocument(restored.document);
    setDraftSaveState("saving");
    setDraftSaveError(null);
    clearSuggestionPopover();
    onTrialHandoffConsumed?.();
  }, [onTrialHandoffConsumed, session, trialHandoffSnapshot]);

  useEffect(() => {
    if (!selectedSpan && activeLayer.primarySpan) {
      setActiveSpanId(activeLayer.primarySpan.id);
      return;
    }

    if (
      activeSpanId &&
      !candidateState.some((span) => span.id === activeSpanId && span.status === "active")
    ) {
      setActiveSpanId(activeLayer.primarySpan?.id ?? null);
    }
  }, [activeLayer.primarySpan, activeSpanId, candidateState, selectedSpan]);

  useEffect(() => {
    if (!session?.authenticated) {
      return;
    }

    if (!draftsHydrated) {
      return;
    }

    if (drafts.length === 0) {
      if (!activeDraftId) {
        setActiveDraftId(`draft_${crypto.randomUUID()}`);
      }
      if (!editorTouched && candidateState.length === 0 && !activeHistoryEntryId) {
        setDocument(INITIAL_DRAFT);
      }
      return;
    }

    if (activeDraftId) {
      return;
    }

    const initialDraft = drafts[0];
    if (!initialDraft) {
      return;
    }

    if (freshDraftAppliedRef.current || trialHandoffAppliedRef.current) {
      return;
    }

    setActiveDraftId(initialDraft.id);
    if (!editorTouched && candidateState.length === 0 && !activeHistoryEntryId) {
      const restored = draftToDocumentState(initialDraft);
      setDocument(restored.document);
      setActiveFeature(restored.feature);
      setCandidateState(restored.candidateState);
      setConfidence(undefined);
      setAnalysisExplanation(restored.explanation);
      setTargetOutput(restored.targetOutput);
      setInterpretedIntent(restored.interpretedIntent);
      setDiagnosticTrace(restored.diagnosticTrace);
      setNativeLanguagePreference(restored.nativeLanguagePreference);
      setTargetLanguagePreference(restored.targetLanguagePreference);
      setFollowInputLanguage(restored.followInputLanguage);
      setLastResolvedLanguagePair(
        restored.resolvedNativeLanguage && restored.resolvedTargetLanguage
          ? {
              nativeLanguage: restored.resolvedNativeLanguage,
              targetLanguage: restored.resolvedTargetLanguage,
            }
          : null,
      );
      setLastAssistRequestDocument(restored.candidateState.length > 0 ? restored.document : null);
    }
  }, [
    activeDraftId,
    activeHistoryEntryId,
    candidateState.length,
    drafts,
    draftsHydrated,
    editorTouched,
    session,
  ]);

  useEffect(() => {
    if (!session?.authenticated || !activeDraftId) {
      return;
    }

    if (document.trim().length === 0) {
      setDraftSaveState("idle");
      setDraftSaveError(null);
      return;
    }

    if (!activeDraft && !editorTouched) {
      setDraftSaveState("idle");
      setDraftSaveError(null);
      return;
    }

    if (draftMatchesCurrentSnapshot) {
      return;
    }

    // Debounced autosave and the explicit Save draft control share the same persist path so users never depend on two subtly different save semantics.
    const timeoutId = window.setTimeout(() => {
      void persistActiveDraftSnapshot();
    }, 800);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    activeDraft,
    activeDraftId,
    activeFeature,
    apiBaseUrl,
    candidateState,
    diagnosticTrace,
    draftMatchesCurrentSnapshot,
    document,
    editorTouched,
    interpretedIntent,
    languageStateKey,
    mobileSession,
    nativeLanguagePreference,
    followInputLanguage,
    session,
    targetOutput,
    targetLanguagePreference,
    lastResolvedLanguagePair,
  ]);

  useEffect(() => {
    if (!hasUnsavedDraftChanges) {
      return;
    }

    function handleBeforeUnload(event: BeforeUnloadEvent) {
      // Autosave is debounced and can fail, so warn only while the current editor snapshot has not reached the draft store.
      event.preventDefault();
      event.returnValue = "";
    }

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedDraftChanges]);

  useEffect(() => {
    function handleDraftFlush(event: Event) {
      const flushEvent = event as CustomEvent<{
        complete?: (saved: boolean) => void;
      }>;

      // Sign-out and other app-shell actions should flush current work explicitly instead of relying on browser unload prompts.
      event.preventDefault();
      void (async () => {
        if (!latestUnsavedDraftRef.current) {
          flushEvent.detail?.complete?.(true);
          return;
        }

        const draftSnapshot = latestDraftSnapshotRef.current;
        if (!draftSnapshot) {
          flushEvent.detail?.complete?.(false);
          return;
        }

        const { apiBaseUrl: latestApiBaseUrl, mobileSession: latestMobileSession } =
          latestPersistContextRef.current;

        try {
          await saveDraft(latestApiBaseUrl, draftSnapshot.id, draftSnapshot, {
            mobileSession: latestMobileSession,
          });
          flushEvent.detail?.complete?.(true);
        } catch (saveError) {
          console.warn("Failed to preserve draft before session change", saveError);
          flushEvent.detail?.complete?.(false);
        }
      })();
    }

    window.addEventListener(WORKBENCH_DRAFT_FLUSH_EVENT, handleDraftFlush);
    return () => {
      window.removeEventListener(WORKBENCH_DRAFT_FLUSH_EVENT, handleDraftFlush);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (!latestUnsavedDraftRef.current || !latestDraftSnapshotRef.current) {
        return;
      }

      const { apiBaseUrl: latestApiBaseUrl, mobileSession: latestMobileSession } =
        latestPersistContextRef.current;

      // Internal SPA navigation unmounts the workbench and cancels the debounce timer, so flush the latest snapshot without touching unmounted React state.
      void saveDraft(
        latestApiBaseUrl,
        latestDraftSnapshotRef.current.id,
        latestDraftSnapshotRef.current,
        { mobileSession: latestMobileSession },
      ).catch((saveError: unknown) => {
        console.warn("Failed to preserve draft during workbench navigation", saveError);
      });
    };
  }, []);

  async function runConvert(documentSnapshot = document, force = false) {
    if (loading || documentSnapshot.trim().length === 0) {
      return;
    }

    if (force && savedRunQuotaLimitReached) {
      setWorkbenchError(
        "Monthly saved-run quota reached. Upgrade to keep saving new workbench runs.",
        "QUOTA_EXCEEDED",
      );
      return;
    }

    if (!force && documentSnapshot === lastAssistRequestDocument) {
      return;
    }

    setLoading(true);
    clearWorkbenchError();
    setLastAssistRequestDocument(documentSnapshot);
    const requestSequence = ++convertRequestSequenceRef.current;
    try {
      const nextResult = await requestAppConvert(
        apiBaseUrl,
        {
          feature: activeFeature,
          sourceInput: documentSnapshot,
          includeExplanation: true,
          ...toConvertRequestLanguages({
            nativeLanguagePreference,
            targetLanguagePreference,
            followInputLanguage,
          }),
          followInputLanguage,
          options: {
            // Reserve durable history entries for explicit runs so background guidance refreshes do not flood the timeline.
            saveHistory: Boolean(session?.authenticated && force),
          },
        },
        { mobileSession },
      );

      if (
        requestSequence !== convertRequestSequenceRef.current ||
        documentRef.current !== documentSnapshot
      ) {
        return;
      }

      const restored = convertResultToDocumentState(nextResult, {
        nativeLanguagePreference,
        targetLanguagePreference,
        followInputLanguage,
      });
      setDocument(restored.document);
      setActiveFeature(restored.feature);
      setCandidateState(restored.candidateState);
      setConfidence(restored.confidence);
      setAnalysisExplanation(restored.explanation);
      setTargetOutput(restored.targetOutput);
      setInterpretedIntent(restored.interpretedIntent);
      setDiagnosticTrace(restored.diagnosticTrace);
      setLastResolvedLanguagePair({
        nativeLanguage: restored.resolvedNativeLanguage ?? nextResult.resolvedNativeLanguage,
        targetLanguage: restored.resolvedTargetLanguage ?? nextResult.resolvedTargetLanguage,
      });
      setActiveResultSource({ kind: "live" });
      setActiveHistoryEntryId(null);
      clearSuggestionPopover();

      if (session?.authenticated) {
        const [historyResult, entitlementResult] = await Promise.allSettled([
          fetchHistory(apiBaseUrl, { mobileSession }),
          fetchEffectiveEntitlement(apiBaseUrl, { mobileSession }),
        ]);
        if (requestSequence !== convertRequestSequenceRef.current) {
          return;
        }

        if (historyResult.status === "fulfilled") {
          setHistory(historyResult.value);
          setHistoryLoadError(null);
        } else {
          // The conversion result is already usable; archive refresh failure must stay scoped to archive UI.
          console.warn(
            "Failed to refresh workbench history after conversion",
            historyResult.reason,
          );
          setHistoryLoadError(
            historyResult.reason instanceof Error
              ? historyResult.reason.message
              : "Saved runs are unavailable",
          );
        }

        if (entitlementResult.status === "fulfilled") {
          setEntitlement(entitlementResult.value);
          setEntitlementError(null);
        } else {
          // Plan refresh is a side effect of a successful run, not proof that the run failed.
          console.warn(
            "Failed to refresh workbench entitlement after conversion",
            entitlementResult.reason,
          );
          setEntitlementError(
            entitlementResult.reason instanceof Error
              ? entitlementResult.reason.message
              : "Plan status is unavailable",
          );
        }
      }
    } catch (submitError) {
      if (requestSequence === convertRequestSequenceRef.current) {
        setWorkbenchError(
          submitError instanceof Error ? submitError.message : "Fluentry run failed",
          submitError instanceof ApiClientError ? submitError.code : null,
        );
      }
    } finally {
      if (requestSequence === convertRequestSequenceRef.current) {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    if (
      activeHistoryEntryId ||
      !editorTouched ||
      document.trim().length === 0 ||
      document === lastAssistRequestDocument
    ) {
      return;
    }

    // Only re-run guidance after an intentional edit so reloads and restored drafts do not silently append duplicate history entries.
    const timeoutId = window.setTimeout(() => {
      void runConvert(document);
    }, 700);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    activeHistoryEntryId,
    document,
    editorTouched,
    followInputLanguage,
    lastAssistRequestDocument,
    nativeLanguagePreference,
    targetLanguagePreference,
  ]);

  useEffect(() => {
    function focusHashTarget() {
      const targetSection = resolveWorkbenchSectionFromHash(window.location.hash);
      if (!targetSection) {
        return;
      }

      const targetElement = window.document.getElementById(
        WORKBENCH_SECTION_DOM_IDS[targetSection],
      );
      if (!targetElement) {
        return;
      }

      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      // Shell-level hash links should land on the correct workbench section even though the target cards mount inside this editor surface.
      window.requestAnimationFrame(() => {
        targetElement.scrollIntoView({
          behavior: prefersReducedMotion ? "auto" : "smooth",
          block: "start",
        });
        targetElement.focus({ preventScroll: true });
      });
    }

    focusHashTarget();
    window.addEventListener("hashchange", focusHashTarget);
    return () => {
      window.removeEventListener("hashchange", focusHashTarget);
    };
  }, []);

  function clearSuggestionPopover() {
    // Clearing all transient popover state keeps stale anchor geometry from surviving after edits or history restores.
    setPopoverPinned(false);
    setPopoverHovered(false);
    setHoveredSpanId(null);
    setActiveSpanId(null);
    setPopoverAnchorRect(null);
  }

  function handleSpanPreviewChange(spanId: string | null, anchorRect: PopoverAnchorRect | null) {
    if (!spanId || !anchorRect) {
      handleSpanPreviewExit();
      return;
    }

    if (popoverPinned) {
      return;
    }

    if (hidePreviewTimeoutRef.current != null) {
      window.clearTimeout(hidePreviewTimeoutRef.current);
      hidePreviewTimeoutRef.current = null;
    }

    setHoveredSpanId(spanId);
    setPopoverAnchorRect(anchorRect);
  }

  function handleSpanPreviewExit() {
    if (popoverPinned || popoverHovered) {
      return;
    }

    hidePreviewTimeoutRef.current = window.setTimeout(() => {
      setHoveredSpanId(null);
      setPopoverAnchorRect(null);
      hidePreviewTimeoutRef.current = null;
    }, 120);
  }

  function handleSpanPinChange(spanId: string | null, anchorRect: PopoverAnchorRect | null) {
    if (!spanId || !anchorRect) {
      if (!popoverHovered) {
        clearSuggestionPopover();
      }
      return;
    }

    if (hidePreviewTimeoutRef.current != null) {
      window.clearTimeout(hidePreviewTimeoutRef.current);
      hidePreviewTimeoutRef.current = null;
    }

    setActiveSpanId(spanId);
    setHoveredSpanId(spanId);
    setPopoverAnchorRect(anchorRect);
    setPopoverPinned(true);
  }

  function handleSelectPopoverSpan(spanId: string) {
    setActiveSpanId(spanId);
    if (!popoverPinned) {
      setHoveredSpanId(spanId);
    }

    const spanElement = window.document.querySelector<HTMLElement>(
      `[data-candidate-span-id="${spanId}"]`,
    );
    if (spanElement) {
      const rect = spanElement.getBoundingClientRect();
      setPopoverAnchorRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        bottom: rect.bottom,
        right: rect.right,
      });
    }
  }

  function restoreHistoryEntry(entry: HistoryEntry) {
    const restored = historyEntryToDocumentState(entry);
    setDocument(restored.document);
    setActiveFeature(restored.feature);
    setCandidateState(restored.candidateState);
    setEditorTouched(false);
    setConfidence(restored.confidence);
    setAnalysisExplanation(restored.explanation);
    setTargetOutput(restored.targetOutput);
    setInterpretedIntent(restored.interpretedIntent);
    setDiagnosticTrace(restored.diagnosticTrace);
    setNativeLanguagePreference(restored.nativeLanguagePreference);
    setTargetLanguagePreference(restored.targetLanguagePreference);
    setFollowInputLanguage(restored.followInputLanguage);
    setLastResolvedLanguagePair(
      restored.resolvedNativeLanguage && restored.resolvedTargetLanguage
        ? {
            nativeLanguage: restored.resolvedNativeLanguage,
            targetLanguage: restored.resolvedTargetLanguage,
          }
        : null,
    );
    // History restore is a replay, not an edit to the previously open draft; autosave may create a new draft only after the user changes it.
    setActiveDraftId(`draft_history_${crypto.randomUUID()}`);
    setActiveHistoryEntryId(entry.id);
    setActiveResultSource({ kind: "history", historyEntryId: entry.id });
    setDraftSaveState("idle");
    setDraftSaveError(null);
    setDraftDeleteState({
      confirmingId: null,
      deletingId: null,
      surface: null,
      error: null,
    });
    setHistoryDeleteState({
      confirmingId: null,
      deletingId: null,
      surface: null,
      error: null,
    });
    clearSuggestionPopover();
    setLastAssistRequestDocument(restored.document);
    clearWorkbenchError();
  }

  function restoreDraftEntry(entry: Draft) {
    const restored = draftToDocumentState(entry);
    setDocument(restored.document);
    setActiveFeature(restored.feature);
    setCandidateState(restored.candidateState);
    setEditorTouched(false);
    setConfidence(undefined);
    setAnalysisExplanation(restored.explanation);
    setTargetOutput(restored.targetOutput);
    setInterpretedIntent(restored.interpretedIntent);
    setDiagnosticTrace(restored.diagnosticTrace);
    setNativeLanguagePreference(restored.nativeLanguagePreference);
    setTargetLanguagePreference(restored.targetLanguagePreference);
    setFollowInputLanguage(restored.followInputLanguage);
    setLastResolvedLanguagePair(
      restored.resolvedNativeLanguage && restored.resolvedTargetLanguage
        ? {
            nativeLanguage: restored.resolvedNativeLanguage,
            targetLanguage: restored.resolvedTargetLanguage,
          }
        : null,
    );
    clearWorkbenchError();
    setActiveDraftId(entry.id);
    setActiveHistoryEntryId(null);
    setActiveResultSource(null);
    setDraftSaveState("idle");
    setDraftSaveError(null);
    setHistoryDeleteState({
      confirmingId: null,
      deletingId: null,
      surface: null,
      error: null,
    });
    clearSuggestionPopover();
    setLastAssistRequestDocument(restored.candidateState.length > 0 ? restored.document : null);
  }

  async function deleteDraftEntry(entry: Draft, surface: "archive" | "resume") {
    setDraftDeleteState({
      confirmingId: entry.id,
      deletingId: entry.id,
      surface,
      error: null,
    });
    try {
      await deleteDraftRequest(apiBaseUrl, entry.id, { mobileSession });
      setDrafts((currentDrafts) => currentDrafts.filter((draft) => draft.id !== entry.id));
      if (activeDraftId === entry.id) {
        const nextDraftId = `draft_${crypto.randomUUID()}`;
        // Deleting the active snapshot means "discard this working state", so reset the canvas instead of autosaving it under a new id.
        setActiveDraftId(nextDraftId);
        setActiveHistoryEntryId(null);
        setActiveResultSource(null);
        setDocument(INITIAL_DRAFT);
        setActiveFeature(INITIAL_FEATURE);
        setCandidateState([]);
        setEditorTouched(false);
        setConfidence(undefined);
        setAnalysisExplanation(undefined);
        setTargetOutput(undefined);
        setInterpretedIntent(undefined);
        setDiagnosticTrace(undefined);
        setNativeLanguagePreference(INITIAL_NATIVE_LANGUAGE);
        setTargetLanguagePreference(INITIAL_TARGET_LANGUAGE);
        setFollowInputLanguage(false);
        setLastResolvedLanguagePair(null);
        setLastAssistRequestDocument(null);
        clearSuggestionPopover();
      }
      setDraftSaveState("idle");
      setDraftSaveError(null);
      setDraftDeleteState({
        confirmingId: null,
        deletingId: null,
        surface: null,
        error: null,
      });
    } catch (deleteError) {
      setDraftDeleteState({
        confirmingId: entry.id,
        deletingId: null,
        surface,
        error: deleteError instanceof Error ? deleteError.message : "Draft delete failed",
      });
    }
  }

  async function deleteHistoryRun(entry: HistoryEntry, surface: "archive" | "resume") {
    setHistoryDeleteState({
      confirmingId: entry.id,
      deletingId: entry.id,
      surface,
      error: null,
    });
    try {
      await deleteHistoryEntryRequest(apiBaseUrl, entry.id, { mobileSession });
      setHistory((currentHistory) =>
        currentHistory.filter((historyEntry) => historyEntry.id !== entry.id),
      );
      if (activeHistoryEntryId === entry.id) {
        // Removing the archive entry should not erase the open text; it only detaches the editor from that saved-run record.
        setActiveHistoryEntryId(null);
        setActiveResultSource(null);
      }
      setHistoryDeleteState({
        confirmingId: null,
        deletingId: null,
        surface: null,
        error: null,
      });
      try {
        setEntitlement(await fetchEffectiveEntitlement(apiBaseUrl, { mobileSession }));
        setEntitlementError(null);
      } catch (entitlementRefreshError) {
        setEntitlementError(
          entitlementRefreshError instanceof Error
            ? entitlementRefreshError.message
            : "Plan status is unavailable",
        );
      }
    } catch (deleteError) {
      setHistoryDeleteState({
        confirmingId: entry.id,
        deletingId: null,
        surface,
        error: deleteError instanceof Error ? deleteError.message : "Saved run delete failed",
      });
    }
  }

  function applyCandidate(spanId: string, candidate: CandidateOption) {
    const nextState = applyCandidateReplacement({
      document,
      candidateSpans: candidateState,
      spanId,
      candidate,
    });
    setDocument(nextState.document);
    setCandidateState(nextState.candidateSpans);
    setEditorTouched(true);
    setTargetOutput(undefined);
    setInterpretedIntent(undefined);
    setDiagnosticTrace(undefined);
    setAnalysisExplanation(undefined);
    clearSuggestionPopover();
    setActiveHistoryEntryId(null);
    setActiveResultSource(null);
    clearWorkbenchError();
  }

  function handleDocumentChange(nextDocument: string) {
    setDocument((currentDocument) => {
      setCandidateState((currentCandidateState) =>
        rebaseCandidateSpansForDocumentChange({
          previousDocument: currentDocument,
          nextDocument,
          candidateSpans: currentCandidateState,
        }),
      );
      return nextDocument;
    });
    setEditorTouched(true);
    markDraftDirty(nextDocument);
    setActiveHistoryEntryId(null);
    setActiveResultSource(null);
    setTargetOutput(undefined);
    setInterpretedIntent(undefined);
    setDiagnosticTrace(undefined);
    setAnalysisExplanation(undefined);
    setPopoverPinned(false);
    setHoveredSpanId(null);
    setPopoverAnchorRect(null);
    clearWorkbenchError();
  }

  function handleFeatureChange(nextFeature: ConvertFeature) {
    if (nextFeature === activeFeature) {
      return;
    }

    // Each mode returns a different inference snapshot, so drop the previous spans and let the touched-edit effect re-run with the new feature.
    setActiveFeature(nextFeature);
    setCandidateState([]);
    setConfidence(undefined);
    setTargetOutput(undefined);
    setInterpretedIntent(undefined);
    setDiagnosticTrace(undefined);
    setAnalysisExplanation(undefined);
    setActiveHistoryEntryId(null);
    setActiveResultSource(null);
    clearSuggestionPopover();
    setLastAssistRequestDocument(null);
    clearWorkbenchError();
    setEditorTouched(true);
  }

  function applyStarterDraft(starter: WorkbenchStarterDraft) {
    // Starter drafts become normal editable work so first-run users can run, autosave, and restore them through the same product path.
    if (!activeDraftId && session?.authenticated) {
      setActiveDraftId(`draft_${crypto.randomUUID()}`);
    }
    setDocument(starter.text);
    setActiveFeature(starter.feature);
    setNativeLanguagePreference(starter.nativeLanguage);
    setTargetLanguagePreference(starter.targetLanguage);
    setFollowInputLanguage(false);
    setCandidateState([]);
    setConfidence(undefined);
    setTargetOutput(undefined);
    setInterpretedIntent(undefined);
    setDiagnosticTrace(undefined);
    setAnalysisExplanation(undefined);
    setLastResolvedLanguagePair(null);
    setLastAssistRequestDocument(null);
    setActiveHistoryEntryId(null);
    setActiveResultSource(null);
    setDraftSaveState("saving");
    setDraftSaveError(null);
    setEditorTouched(true);
    clearSuggestionPopover();
    clearWorkbenchError();
  }

  function useResultAsNextDraft(nextDocument: string) {
    // A generated sentence often needs one more pass, so promote it back into the editor as ordinary saved work instead of ending the flow at export.
    setActiveDraftId(`draft_result_${crypto.randomUUID()}`);
    setActiveHistoryEntryId(null);
    setActiveResultSource(null);
    setDocument(nextDocument);
    setCandidateState([]);
    setEditorTouched(true);
    setConfidence(undefined);
    setAnalysisExplanation(undefined);
    setTargetOutput(undefined);
    setInterpretedIntent(undefined);
    setDiagnosticTrace(undefined);
    if (lastResolvedLanguagePair?.targetLanguage) {
      setNativeLanguagePreference(lastResolvedLanguagePair.targetLanguage);
      setTargetLanguagePreference(lastResolvedLanguagePair.targetLanguage);
      setFollowInputLanguage(false);
    }
    setLastResolvedLanguagePair(null);
    setLastAssistRequestDocument(null);
    clearSuggestionPopover();
    clearWorkbenchError();
    markDraftDirty();
  }

  async function copySavedResultBrief(key: string, args: ResultExportPayload) {
    try {
      // Saved cards use the same portable brief as the live result so external handoff stays predictable.
      await writeClipboardText(buildResultExportText(args));
      setShareActionState({ key, action: "copy", status: "done" });
      window.setTimeout(
        () => setShareActionState({ key: null, action: null, status: "idle" }),
        2_000,
      );
    } catch {
      setShareActionState({ key, action: "copy", status: "error" });
    }
  }

  function downloadSavedResultBrief(key: string, args: ResultExportPayload) {
    try {
      // Download mirrors the live result export for saved work that needs to move into docs, tickets, or notes.
      downloadResultText(args);
      setShareActionState({ key, action: "download", status: "done" });
      window.setTimeout(
        () => setShareActionState({ key: null, action: null, status: "idle" }),
        2_000,
      );
    } catch {
      setShareActionState({ key, action: "download", status: "error" });
    }
  }

  function renderSavedResultBriefActions(
    key: string,
    payload: ResultExportPayload,
    className = "min-h-11 w-full justify-center rounded-full px-4 text-[#0b6d70] hover:bg-white/80 hover:text-[#0b6d70] sm:w-auto",
  ): ReactNode {
    const copyLabel =
      shareActionState.key === key && shareActionState.action === "copy"
        ? shareActionState.status === "error"
          ? "Copy failed"
          : "Brief copied"
        : "Copy brief";
    const downloadLabel =
      shareActionState.key === key && shareActionState.action === "download"
        ? shareActionState.status === "error"
          ? "Download failed"
          : "Downloaded"
        : "Download";

    return (
      <>
        <Button
          type="button"
          variant="ghost"
          className={className}
          onClick={() => {
            void copySavedResultBrief(key, payload);
          }}
        >
          {shareActionState.key === key &&
          shareActionState.action === "copy" &&
          shareActionState.status === "done" ? (
            <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
          ) : shareActionState.key === key &&
            shareActionState.action === "copy" &&
            shareActionState.status === "error" ? (
            <AlertCircle className="mr-1.5 h-3.5 w-3.5" />
          ) : (
            <Copy className="mr-1.5 h-3.5 w-3.5" />
          )}
          {copyLabel}
        </Button>
        <Button
          type="button"
          variant="ghost"
          className={className}
          onClick={() => downloadSavedResultBrief(key, payload)}
        >
          {shareActionState.key === key &&
          shareActionState.action === "download" &&
          shareActionState.status === "done" ? (
            <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
          ) : shareActionState.key === key &&
            shareActionState.action === "download" &&
            shareActionState.status === "error" ? (
            <AlertCircle className="mr-1.5 h-3.5 w-3.5" />
          ) : (
            <Download className="mr-1.5 h-3.5 w-3.5" />
          )}
          {downloadLabel}
        </Button>
      </>
    );
  }

  function renderDraftDeleteActions(entry: Draft, surface: "archive" | "resume"): ReactNode {
    const isConfirming =
      draftDeleteState.confirmingId === entry.id && draftDeleteState.surface === surface;
    const isDeleting = draftDeleteState.deletingId === entry.id;

    if (isConfirming) {
      return (
        <>
          <Button
            type="button"
            variant="destructive"
            className="min-h-11 w-full rounded-full px-4 sm:w-auto"
            disabled={isDeleting}
            onClick={() => {
              void deleteDraftEntry(entry, surface);
            }}
          >
            {isDeleting ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
            )}
            Confirm delete
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="min-h-11 w-full rounded-full px-4 text-[#648084] sm:w-auto"
            disabled={isDeleting}
            onClick={() =>
              setDraftDeleteState({
                confirmingId: null,
                deletingId: null,
                surface: null,
                error: null,
              })
            }
          >
            Cancel
          </Button>
        </>
      );
    }

    return (
      <Button
        type="button"
        variant="ghost"
        className="min-h-11 w-full rounded-full px-4 text-[#9a3412] hover:bg-orange-50 hover:text-[#9a3412] sm:w-auto"
        aria-label={`Delete draft: ${entry.sourceInput}`}
        onClick={() =>
          setDraftDeleteState({
            confirmingId: entry.id,
            deletingId: null,
            surface,
            error: null,
          })
        }
      >
        <Trash2 className="mr-1.5 h-3.5 w-3.5" />
        Delete draft
      </Button>
    );
  }

  function renderHistoryDeleteActions(
    entry: HistoryEntry,
    surface: "archive" | "resume",
  ): ReactNode {
    const isConfirming =
      historyDeleteState.confirmingId === entry.id && historyDeleteState.surface === surface;
    const isDeleting = historyDeleteState.deletingId === entry.id;

    if (isConfirming) {
      return (
        <>
          <Button
            type="button"
            variant="destructive"
            className="min-h-11 w-full rounded-full px-4 sm:w-auto"
            disabled={isDeleting}
            onClick={() => {
              void deleteHistoryRun(entry, surface);
            }}
          >
            {isDeleting ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
            )}
            Confirm delete
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="min-h-11 w-full rounded-full px-4 text-[#648084] sm:w-auto"
            disabled={isDeleting}
            onClick={() =>
              setHistoryDeleteState({
                confirmingId: null,
                deletingId: null,
                surface: null,
                error: null,
              })
            }
          >
            Cancel
          </Button>
        </>
      );
    }

    return (
      <Button
        type="button"
        variant="ghost"
        className="min-h-11 w-full rounded-full px-4 text-[#8b5b5b] hover:bg-red-50 hover:text-red-700 sm:w-auto"
        disabled={isDeleting}
        aria-label={`Delete run: ${entry.sourceInput}`}
        onClick={() =>
          setHistoryDeleteState({
            confirmingId: entry.id,
            deletingId: null,
            surface,
            error: null,
          })
        }
      >
        <Trash2 className="mr-1.5 h-3.5 w-3.5" />
        Delete run
      </Button>
    );
  }

  const canSwapLanguages = canSwapLanguagePreferences({
    nativeLanguagePreference,
    targetLanguagePreference,
    followInputLanguage,
    lastResolvedLanguagePair,
  });
  const trimmedDocument = document.trim();
  const showStarterDrafts = trimmedDocument.length === 0 && !activeResultSource;
  const primaryRunLabel = savedRunQuotaLimitReached
    ? "Saved-run limit reached"
    : trimmedDocument.length === 0
      ? "Add a draft first"
      : targetOutput
        ? "Save run"
        : "Run Fluentry";
  const latestDraft = session?.authenticated ? drafts[0] : undefined;
  const latestHistoryEntry = session?.authenticated ? history[0] : undefined;
  const latestDraftSharePayload = latestDraft ? draftSharePayload(latestDraft) : null;
  const latestHistorySharePayload = latestHistoryEntry
    ? historySharePayload(latestHistoryEntry)
    : null;
  const hasResumeQueue =
    session?.authenticated &&
    (latestDraft !== undefined || latestHistoryEntry !== undefined || targetOutput !== undefined);
  const headerIdentityLabel = session?.user?.email ?? "Guest";
  const highlightHint = coarsePointer ? "Tap highlights" : "Review highlights";
  const conciseStatusDetail =
    status.tone === "loading"
      ? "Checking ranges..."
      : status.tone === "success"
        ? "Open highlights to refine."
        : status.tone === "history"
          ? "Restored without rerunning."
          : status.tone === "error"
            ? status.detail
            : "Run when ready.";
  return (
    <div className="grid gap-5 sm:gap-6">
      <section id="editor" className="scroll-mt-24" tabIndex={-1}>
        {/* Keep the first surface anchored on drafting so shell actions can send people back to the active task. */}
        <PanelCard className="overflow-hidden rounded-[1.5rem] border border-[#0d8b87]/18 bg-white/82 shadow-[0_18px_50px_-34px_rgba(11,109,112,0.45)]">
          <div className="px-4 py-4 sm:px-5 sm:py-5">
            {activeResultSource?.kind === "trial-handoff" ? (
              <div className="mb-4 rounded-[1.25rem] border border-[#0d8b87]/20 bg-[#e8f5f2] px-4 py-3 text-sm leading-6 text-[#245c5e]">
                <div>
                  <span className="font-semibold text-[#0b6d70]">Trial result resumed.</span> Keep
                  editing here, or save the restored draft.
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] font-semibold tracking-[0.12em] text-[#0b6d70] uppercase">
                  <span className="rounded-full border border-[#0d8b87]/20 bg-white/70 px-3 py-1">
                    {candidateState.length} reviewed highlights
                  </span>
                  <a
                    className="rounded-full border border-[#0d8b87]/30 bg-[#0d8b87] px-3 py-1 text-white transition hover:bg-[#0b6d70]"
                    href={`#${WORKBENCH_SECTION_DOM_IDS.drafts}`}
                  >
                    View draft snapshot
                  </a>
                </div>
              </div>
            ) : null}
            <div className="flex flex-col overflow-hidden rounded-[1.35rem] border border-[#0f766e]/10 bg-[#f3f7f6] shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]">
              <div className="px-3 pt-3 sm:px-4 sm:pt-4">
                {/* Intent: keep the editor first so the workbench reads like a translation tool instead of a control dashboard. */}
                <CandidateEditor
                  ariaLabel="Workbench draft editor"
                  document={document}
                  visibleSpans={activeLayer.visibleSpans}
                  activeSpanId={selectedSpan?.id ?? null}
                  onDocumentChange={handleDocumentChange}
                  onSelectionRangeChange={setSelectionRange}
                  onSpanClick={handleSpanPinChange}
                  onSpanPreviewChange={handleSpanPreviewChange}
                  onSubmitShortcut={() => {
                    void runConvert(document, true);
                  }}
                />
              </div>

              <div className="grid gap-3 border-t border-[#0f766e]/8 px-3 py-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end lg:justify-between">
                <div className="grid gap-2">
                  <div className="flex flex-wrap items-center gap-2 text-[11px] text-[#648084]">
                    <span className="inline-flex items-center gap-1 rounded-full border border-[#0f766e]/10 bg-white/74 px-2.5 py-1">
                      {document.length} chars
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full border border-[#0f766e]/10 bg-white/74 px-2.5 py-1">
                      <WandSparkles className="h-3.5 w-3.5 text-[#0b6d70]" />
                      {highlightHint}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full border border-[#0f766e]/10 bg-white/74 px-2.5 py-1">
                      <Command className="h-3.5 w-3.5" />
                      Cmd + Enter
                    </span>
                    <span className="max-w-full truncate text-[11px] text-[#7a9194] sm:max-w-[18rem]">
                      {headerIdentityLabel}
                    </span>
                  </div>

                  <div
                    className={[
                      "inline-flex items-center gap-2 text-xs",
                      status.tone === "error"
                        ? "text-red-700"
                        : status.tone === "success"
                          ? "text-[#0b6d70]"
                          : status.tone === "history"
                            ? "text-slate-700"
                            : "text-[#648084]",
                    ].join(" ")}
                  >
                    {status.tone === "error" ? (
                      <AlertCircle className="h-4 w-4" />
                    ) : status.tone === "loading" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : status.tone === "history" ? (
                      <History className="h-4 w-4" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                    <span className="font-medium">{status.label}</span>
                    <span className="text-[#7a9194]">{conciseStatusDetail}</span>
                  </div>

                  {session?.authenticated && activeDraftId ? (
                    <div
                      className={[
                        "inline-flex items-center gap-2 text-xs",
                        draftSaveState === "error"
                          ? "text-red-700"
                          : draftSaveState === "dirty"
                            ? "text-orange-700"
                            : draftSaveState === "saved"
                              ? "text-[#0b6d70]"
                              : "text-[#648084]",
                      ].join(" ")}
                    >
                      {draftSaveState === "saving" ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : draftSaveState === "saved" ? (
                        <CheckCircle2 className="h-4 w-4" />
                      ) : draftSaveState === "error" ? (
                        <AlertCircle className="h-4 w-4" />
                      ) : draftSaveState === "dirty" ? (
                        <FileText className="h-4 w-4 text-orange-600" />
                      ) : (
                        <FileText className="h-4 w-4" />
                      )}
                      <span className="font-medium">
                        {draftSaveState === "saving"
                          ? "Saving..."
                          : draftSaveState === "saved"
                            ? "Saved"
                            : draftSaveState === "error"
                              ? "Save failed"
                              : draftSaveState === "dirty"
                                ? "Unsaved changes"
                                : "Autosave ready"}
                      </span>
                      {draftSaveError ? (
                        <span className="hidden text-[#648084] md:inline">{draftSaveError}</span>
                      ) : null}
                    </div>
                  ) : null}

                  {showQuotaRecovery ? (
                    <div className="flex flex-wrap items-center gap-2 rounded-[0.95rem] border border-orange-200/80 bg-orange-50/80 px-3 py-2 text-xs text-orange-950">
                      <AlertCircle className="h-4 w-4" />
                      <span className="font-medium">Saved-run quota reached.</span>
                      <span className="text-orange-900/80">Upgrade to save more runs.</span>
                      {renderBillingCta(
                        "font-semibold text-[#0b6d70] underline underline-offset-4",
                        "Compare plans",
                        quotaBillingUrl,
                      )}
                    </div>
                  ) : null}
                </div>

                <div className="grid gap-2 sm:flex sm:flex-wrap sm:items-center sm:justify-end">
                  {session?.authenticated && activeDraftId ? (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={trimmedDocument.length === 0}
                      className="min-h-11 w-full rounded-full border-[#0f766e]/10 bg-white/80 px-5 text-sm font-semibold text-[#0b6d70] shadow-none hover:bg-white sm:w-auto"
                      onClick={() => {
                        void persistActiveDraftSnapshot();
                      }}
                    >
                      {draftSaveState === "saving" ? (
                        <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                      ) : draftSaveState === "saved" ? (
                        <CheckCircle2 className="mr-1.5 h-4 w-4" />
                      ) : (
                        <FileText className="mr-1.5 h-4 w-4" />
                      )}
                      Save draft
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    disabled={loading || trimmedDocument.length === 0 || savedRunQuotaLimitReached}
                    className="min-h-12 w-full rounded-full bg-[#0d8b87] px-6 text-sm font-semibold text-white hover:bg-[#0b7774] sm:w-auto"
                    onClick={() => {
                      void runConvert(document, true);
                    }}
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Reading draft
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-[#b9efec]" />
                        {primaryRunLabel}
                      </span>
                    )}
                  </Button>
                </div>
              </div>

              {showStarterDrafts ? (
                <div className="border-t border-[#0f766e]/8 px-3 py-3 sm:px-4">
                  <div className="grid gap-2 rounded-[1rem] border border-[#0f766e]/8 bg-white/38 p-2.5">
                    <span className="text-[10px] font-semibold tracking-[0.18em] text-[#6a8588] uppercase">
                      Starters
                    </span>
                    <div className="grid gap-2 md:grid-cols-3">
                      {WORKBENCH_STARTER_DRAFTS.map((starter) => (
                        <button
                          key={starter.id}
                          type="button"
                          className="grid gap-1 rounded-[0.8rem] border border-[#0f766e]/8 bg-white/64 px-3 py-2.5 text-left transition-colors hover:border-[#8fd1ca] hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d8b87]/30"
                          onClick={() => applyStarterDraft(starter)}
                        >
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0b6d70]">
                            <WandSparkles className="h-3.5 w-3.5" />
                            {starter.label}
                          </span>
                          <span className="line-clamp-1 text-xs leading-5 text-[#51696d]">
                            {starter.text}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="grid gap-3 border-t border-[#0f766e]/8 px-3 py-3 sm:px-4 sm:py-4">
                <div className="grid gap-3 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
                  <FeatureControls
                    value={activeFeature}
                    disabled={loading}
                    onChange={handleFeatureChange}
                  />
                  <LanguageControls
                    nativeLanguage={nativeLanguagePreference}
                    targetLanguage={targetLanguagePreference}
                    followInputLanguage={followInputLanguage}
                    canSwap={canSwapLanguages}
                    onNativeLanguageChange={(value) => {
                      setNativeLanguagePreference(value);
                      setLastAssistRequestDocument(null);
                      setTargetOutput(undefined);
                      setInterpretedIntent(undefined);
                      setDiagnosticTrace(undefined);
                      setAnalysisExplanation(undefined);
                      setEditorTouched(true);
                      markDraftDirty();
                    }}
                    onTargetLanguageChange={(value) => {
                      setTargetLanguagePreference(value);
                      setLastAssistRequestDocument(null);
                      setTargetOutput(undefined);
                      setInterpretedIntent(undefined);
                      setDiagnosticTrace(undefined);
                      setAnalysisExplanation(undefined);
                      setEditorTouched(true);
                      markDraftDirty();
                    }}
                    onFollowInputLanguageChange={(value) => {
                      setFollowInputLanguage(value);
                      setLastAssistRequestDocument(null);
                      setTargetOutput(undefined);
                      setInterpretedIntent(undefined);
                      setDiagnosticTrace(undefined);
                      setAnalysisExplanation(undefined);
                      setEditorTouched(true);
                      markDraftDirty();
                    }}
                    onSwap={() => {
                      const nextPreferences = swapLanguagePreferences({
                        nativeLanguagePreference,
                        targetLanguagePreference,
                        followInputLanguage,
                        lastResolvedLanguagePair,
                      });
                      setNativeLanguagePreference(nextPreferences.nativeLanguagePreference);
                      setTargetLanguagePreference(nextPreferences.targetLanguagePreference);
                      setFollowInputLanguage(nextPreferences.followInputLanguage);
                      setLastAssistRequestDocument(null);
                      setTargetOutput(undefined);
                      setInterpretedIntent(undefined);
                      setDiagnosticTrace(undefined);
                      setAnalysisExplanation(undefined);
                      setEditorTouched(true);
                      markDraftDirty();
                    }}
                  />
                </div>
              </div>

              <CandidatePopover
                anchorRect={popoverAnchorRect}
                selectedSpan={selectedSpan}
                competingSpans={activeLayer.competingSpans}
                explanation={analysisExplanation}
                confidence={confidence}
                pinned={popoverPinned}
                onSelectSpan={handleSelectPopoverSpan}
                onApplyCandidate={applyCandidate}
                onClose={clearSuggestionPopover}
                onPointerEnter={() => {
                  if (hidePreviewTimeoutRef.current != null) {
                    window.clearTimeout(hidePreviewTimeoutRef.current);
                    hidePreviewTimeoutRef.current = null;
                  }
                  setPopoverHovered(true);
                }}
                onPointerLeave={() => {
                  setPopoverHovered(false);
                  handleSpanPreviewExit();
                }}
              />
            </div>
          </div>
        </PanelCard>
      </section>

      <ConvertResultSummary
        sourceInput={document}
        interpretedIntent={interpretedIntent}
        targetOutput={targetOutput}
        diagnosticTrace={diagnosticTrace}
        confidence={confidence}
        resolvedTargetLanguage={lastResolvedLanguagePair?.targetLanguage}
        onUseAsDraft={useResultAsNextDraft}
      />

      {hasResumeQueue ? (
        <PanelCard
          role="region"
          aria-label="Resume queue"
          className="grid gap-4 rounded-[1.5rem] border border-[#0f766e]/8 bg-white/72 p-4 shadow-sm shadow-[#aabfc0]/12"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="grid gap-1">
              <p className="text-[10px] font-semibold tracking-[0.24em] text-[#5f7c80] uppercase">
                Resume queue
              </p>
              <h2 className="m-0 font-serif text-lg tracking-tight text-slate-950">Resume</h2>
            </div>
            <Badge
              variant="outline"
              className="rounded-full border-[#0f766e]/8 bg-[#f7faf9] text-[10px] font-semibold tracking-[0.16em] text-[#0b6d70] uppercase shadow-none"
            >
              {[
                latestDraft ? "draft" : null,
                latestHistoryEntry ? "run" : null,
                targetOutput ? "result" : null,
              ]
                .filter((entry): entry is string => entry !== null)
                .join(" / ")}
            </Badge>
          </div>

          <div className="grid gap-3 lg:grid-cols-3">
            {latestDraft ? (
              <div className="grid gap-3 rounded-[1.25rem] border border-[#0f766e]/8 bg-[#f7faf9] p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="grid gap-1">
                    <span className="text-[10px] font-semibold tracking-[0.2em] text-[#5f7c80] uppercase">
                      Latest draft
                    </span>
                    <strong className="line-clamp-2 text-sm font-medium text-slate-950">
                      {latestDraft.sourceInput}
                    </strong>
                  </div>
                  <span className="shrink-0 text-[11px] text-[#648084]">
                    {formatHistoryTimestamp(latestDraft.updatedAt)}
                  </span>
                </div>
                <p className="m-0 line-clamp-2 text-sm leading-6 text-[#557074]">
                  {getDraftPreview(latestDraft)}
                </p>
                <div className="grid gap-2 sm:flex sm:flex-wrap sm:items-center">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="min-h-11 w-full rounded-full border-[#0f766e]/10 bg-white/80 text-[#0b6d70] shadow-none hover:bg-white sm:w-auto"
                    onClick={() => restoreDraftEntry(latestDraft)}
                  >
                    <ArrowRight className="mr-1.5 h-3.5 w-3.5" />
                    Continue draft
                  </Button>
                  {latestDraftSharePayload
                    ? renderSavedResultBriefActions(
                        `resume-draft:${latestDraft.id}`,
                        latestDraftSharePayload,
                      )
                    : null}
                  {renderDraftDeleteActions(latestDraft, "resume")}
                </div>
                {draftDeleteState.error &&
                draftDeleteState.confirmingId === latestDraft.id &&
                draftDeleteState.surface === "resume" ? (
                  <p className="m-0 text-xs text-red-700">{draftDeleteState.error}</p>
                ) : null}
              </div>
            ) : null}

            {latestHistoryEntry ? (
              <div className="grid gap-3 rounded-[1.25rem] border border-[#0f766e]/8 bg-[#f7faf9] p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="grid gap-1">
                    <span className="text-[10px] font-semibold tracking-[0.2em] text-[#5f7c80] uppercase">
                      Latest saved run
                    </span>
                    <strong className="line-clamp-2 text-sm font-medium text-slate-950">
                      {latestHistoryEntry.sourceInput}
                    </strong>
                  </div>
                  <span className="shrink-0 text-[11px] text-[#648084]">
                    {formatHistoryTimestamp(latestHistoryEntry.createdAt)}
                  </span>
                </div>
                <p className="m-0 line-clamp-2 text-sm leading-6 text-[#557074]">
                  {latestHistoryEntry.targetOutput}
                </p>
                <div className="grid gap-2 sm:flex sm:flex-wrap sm:items-center">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="min-h-11 w-full rounded-full border-[#0f766e]/10 bg-white/80 text-[#0b6d70] shadow-none hover:bg-white sm:w-auto"
                    onClick={() => restoreHistoryEntry(latestHistoryEntry)}
                  >
                    <ArrowRight className="mr-1.5 h-3.5 w-3.5" />
                    Continue run
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="rounded-full text-[#0b6d70] hover:bg-white/80 hover:text-[#0b6d70]"
                    onClick={() => useResultAsNextDraft(latestHistoryEntry.targetOutput)}
                  >
                    <FileText className="mr-1.5 h-3.5 w-3.5" />
                    Use result as draft
                  </Button>
                  {latestHistorySharePayload
                    ? renderSavedResultBriefActions(
                        `resume-history:${latestHistoryEntry.id}`,
                        latestHistorySharePayload,
                      )
                    : null}
                  {renderHistoryDeleteActions(latestHistoryEntry, "resume")}
                </div>
                {historyDeleteState.error &&
                historyDeleteState.confirmingId === latestHistoryEntry.id &&
                historyDeleteState.surface === "resume" ? (
                  <p className="m-0 text-xs font-medium text-red-700">{historyDeleteState.error}</p>
                ) : null}
              </div>
            ) : null}

            {targetOutput ? (
              <div className="grid gap-3 rounded-[1.25rem] border border-[#0f766e]/8 bg-[#f7faf9] p-4">
                <div className="grid gap-1">
                  <span className="text-[10px] font-semibold tracking-[0.2em] text-[#5f7c80] uppercase">
                    Current result
                  </span>
                  <strong className="line-clamp-2 text-sm font-medium text-slate-950">
                    Ready for another pass
                  </strong>
                </div>
                <p className="m-0 line-clamp-2 text-sm leading-6 text-[#557074]">{targetOutput}</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="min-h-11 w-full rounded-full border-[#0f766e]/10 bg-white/80 text-[#0b6d70] shadow-none hover:bg-white sm:w-fit"
                  onClick={() => useResultAsNextDraft(targetOutput)}
                >
                  <FileText className="mr-1.5 h-3.5 w-3.5" />
                  Start next draft
                </Button>
              </div>
            ) : null}
          </div>
        </PanelCard>
      ) : null}

      <PanelCard className="grid gap-4 rounded-[1.5rem] border border-[#0f766e]/8 bg-white/72 p-4 shadow-sm shadow-[#aabfc0]/12">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="grid gap-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant="outline"
                className={[
                  "rounded-full border-[#0f766e]/10 text-[10px] font-semibold tracking-[0.18em] uppercase shadow-none",
                  savedRunQuotaLimitReached
                    ? "bg-[#fff4ed] text-[#9a3412]"
                    : isPaidPlan
                      ? "bg-[#e8f5f2] text-[#0b6d70]"
                      : "bg-[#f4f8f7] text-[#5f7c80]",
                ].join(" ")}
              >
                {planStatusLabel}
              </Badge>
              {entitlementError ? (
                <span className="text-xs font-medium text-red-700">{entitlementError}</span>
              ) : null}
            </div>
            <h2 className="m-0 font-serif text-lg tracking-tight text-slate-950">
              {savedRunQuotaLimitReached ? "Saved-run limit reached" : "Plan and saved work"}
            </h2>
            <p className="m-0 max-w-3xl text-sm leading-6 text-[#648084]">
              {savedRunQuotaLimitReached
                ? "You can still edit, but new saved runs stay paused until the limit resets or the plan changes."
                : isPaidPlan
                  ? "Drafts, saved runs, and restore stay available across sessions."
                  : "Free access covers the core flow. Upgrade when you need more saved work and longer history."}
            </p>
          </div>
          <Button
            asChild
            className="h-10 shrink-0 rounded-full bg-[#0d8b87] px-5 text-sm font-semibold text-white hover:bg-[#0b7774]"
          >
            {renderBillingCta(undefined, billingCtaLabel)}
          </Button>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <div className="rounded-[1rem] border border-[#0f766e]/8 bg-[#f7faf9] px-4 py-3">
            <p className="m-0 text-[10px] font-semibold tracking-[0.18em] text-[#5f7c80] uppercase">
              Drafts
            </p>
            <p className="mt-1 text-lg font-semibold text-slate-950">
              {draftLoadError ? "Unavailable" : drafts.length}
            </p>
            <p className="m-0 text-xs leading-5 text-[#648084]">
              {draftLoadError ? "Draft archive could not be loaded" : "Restorable work"}
            </p>
          </div>
          <div className="rounded-[1rem] border border-[#0f766e]/8 bg-[#f7faf9] px-4 py-3">
            <p className="m-0 text-[10px] font-semibold tracking-[0.18em] text-[#5f7c80] uppercase">
              Runs
            </p>
            <p className="mt-1 text-lg font-semibold text-slate-950">
              {historyLoadError ? "Unavailable" : savedRunQuotaLabel}
            </p>
            <p className="m-0 text-xs leading-5 text-[#648084]">
              {historyLoadError ? "Saved run archive could not be loaded" : savedRunQuotaDetail}
            </p>
          </div>
        </div>
      </PanelCard>

      {session?.authenticated ? (
        <PanelCard
          id={WORKBENCH_SECTION_DOM_IDS.drafts}
          aria-labelledby="workbench-drafts-heading"
          tabIndex={-1}
          className="scroll-mt-24 rounded-[1.5rem] border border-[#0f766e]/10 bg-white/78 shadow-sm shadow-[#aabfc0]/28 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d8b87]/30"
        >
          <div className="flex items-center justify-between gap-3 border-b border-[#0f766e]/10 px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#0f766e]/10 bg-[#f4f8f7] text-[#0b6d70]">
                <FileText className="h-4 w-4" />
              </div>
              <div className="grid gap-0.5">
                <p className="text-[10px] font-semibold tracking-[0.24em] text-[#5f7c80] uppercase">
                  Drafts
                </p>
                <h2 id="workbench-drafts-heading" className="text-sm font-semibold text-slate-950">
                  Saved drafts
                </h2>
              </div>
            </div>
          </div>

          <div className="grid gap-3 p-5 md:grid-cols-2 xl:grid-cols-3">
            {draftLoadError ? (
              <div className="rounded-[1.25rem] border border-red-200 bg-red-50 py-10 md:col-span-2 xl:col-span-3">
                <EmptyState
                  title="Draft snapshots unavailable"
                  description={`${draftLoadError}. Your saved drafts may still exist; retry after the archive service recovers.`}
                />
              </div>
            ) : drafts.length === 0 ? (
              <div className="rounded-[1.25rem] border border-dashed border-[#8fd1ca]/50 bg-[#f3f7f6] py-10 md:col-span-2 xl:col-span-3">
                <EmptyState
                  title="No drafts yet"
                  description="Autosave will create restorable draft snapshots as you keep typing."
                />
              </div>
            ) : (
              drafts.map((entry) => {
                const isActive = activeDraftId === entry.id;
                const isConfirmingDelete =
                  draftDeleteState.confirmingId === entry.id &&
                  draftDeleteState.surface === "archive";
                const isDeleting = draftDeleteState.deletingId === entry.id;
                const draftAction = summarizeDraftAction(entry);
                const draftPreview = getDraftPreview(entry);
                const sharePayload = draftSharePayload(entry);
                const shareKey = `draft:${entry.id}`;
                return (
                  <div
                    key={entry.id}
                    className={[
                      "grid gap-3 rounded-[1.25rem] border p-4 text-left transition-colors",
                      isActive
                        ? "border-[#8fd1ca] bg-[#e8f6f4]"
                        : "border-[#0f766e]/10 bg-[#f4f8f7] hover:border-[#9dc7c3] hover:bg-white",
                    ].join(" ")}
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="grid gap-1">
                        <span className="text-[10px] font-semibold tracking-[0.2em] text-[#5f7c80] uppercase">
                          {draftAction}
                        </span>
                        <strong className="line-clamp-2 text-sm font-medium text-slate-950">
                          {entry.sourceInput}
                        </strong>
                      </div>
                      <span className="shrink-0 text-[11px] text-[#648084]">
                        {formatHistoryTimestamp(entry.updatedAt)}
                      </span>
                    </div>
                    <div className="grid gap-2 rounded-[1rem] border border-[#0f766e]/10 bg-white/72 px-3 py-2">
                      <span className="text-[10px] font-semibold tracking-[0.18em] text-[#5f7c80] uppercase">
                        {entry.targetOutput
                          ? "Reusable result"
                          : entry.interpretedIntent
                            ? "Intent"
                            : "Draft preview"}
                      </span>
                      <p className="m-0 line-clamp-3 text-sm leading-6 text-[#385d62]">
                        {draftPreview}
                      </p>
                    </div>
                    <div className="grid gap-2 sm:flex sm:flex-wrap sm:items-center sm:justify-between">
                      <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold tracking-[0.12em] text-[#0b6d70] uppercase">
                        <span className="rounded-full border border-[#0f766e]/10 bg-white/80 px-2.5 py-1">
                          {entry.alternatives.length} highlighted ranges
                        </span>
                        {entry.diagnosticTrace?.length ? (
                          <span className="rounded-full border border-[#0f766e]/10 bg-white/80 px-2.5 py-1">
                            {entry.diagnosticTrace.length} trace steps
                          </span>
                        ) : null}
                      </div>
                      <div className="grid gap-2 sm:flex sm:flex-wrap sm:items-center">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="min-h-11 w-full rounded-full border-[#0f766e]/10 bg-white/80 text-[#0b6d70] shadow-none hover:bg-white sm:w-auto"
                          disabled={isDeleting}
                          onClick={() => restoreDraftEntry(entry)}
                        >
                          <ArrowRight className="mr-1.5 h-3.5 w-3.5" />
                          Continue
                        </Button>
                        {sharePayload
                          ? renderSavedResultBriefActions(shareKey, sharePayload)
                          : null}
                        {renderDraftDeleteActions(entry, "archive")}
                      </div>
                    </div>
                    {draftDeleteState.error && isConfirmingDelete ? (
                      <p className="m-0 text-xs text-red-700">{draftDeleteState.error}</p>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        </PanelCard>
      ) : null}

      <PanelCard
        id={WORKBENCH_SECTION_DOM_IDS.history}
        aria-labelledby="workbench-history-heading"
        tabIndex={-1}
        className="scroll-mt-24 rounded-[1.5rem] border border-[#0f766e]/10 bg-white/78 shadow-sm shadow-[#aabfc0]/28 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0d8b87]/30"
      >
        <div className="flex items-center justify-between gap-3 border-b border-[#0f766e]/10 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#0f766e]/10 bg-[#f4f8f7] text-[#0b6d70]">
              <Clock className="h-4 w-4" />
            </div>
            <div className="grid gap-0.5">
              <p className="text-[10px] font-semibold tracking-[0.24em] text-[#5f7c80] uppercase">
                History
              </p>
              <h2 id="workbench-history-heading" className="text-sm font-semibold text-slate-950">
                Saved runs
              </h2>
            </div>
          </div>
        </div>

        <div className="grid gap-3 p-5">
          {historyLoadError ? (
            <div className="rounded-[1.25rem] border border-red-200 bg-red-50 py-12">
              <EmptyState
                title="Saved runs unavailable"
                description={`${historyLoadError}. Your previous Fluentry passes may still exist; retry after the archive service recovers.`}
              />
            </div>
          ) : history.length === 0 ? (
            <div className="rounded-[1.25rem] border border-dashed border-[#8fd1ca]/50 bg-[#f3f7f6] py-12">
              <EmptyState
                title="No runs yet"
                description="Your saved Fluentry passes will appear here as a vertical timeline."
              />
            </div>
          ) : (
            history.map((entry) => {
              const isActive = activeHistoryEntryId === entry.id;
              const isConfirmingDelete =
                historyDeleteState.confirmingId === entry.id &&
                historyDeleteState.surface === "archive";
              const sharePayload = historySharePayload(entry);
              const shareKey = `history:${entry.id}`;

              return (
                <div
                  key={entry.id}
                  className={[
                    "grid gap-3 rounded-[1.25rem] border p-4 text-left transition-colors",
                    isActive
                      ? "border-[#8fd1ca] bg-[#e8f6f4]"
                      : "border-[#0f766e]/10 bg-[#f4f8f7] hover:border-[#9dc7c3] hover:bg-white",
                  ].join(" ")}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="grid gap-1">
                      <span className="text-[10px] font-semibold tracking-[0.2em] text-[#5f7c80] uppercase">
                        Result saved
                      </span>
                      <strong className="line-clamp-2 text-sm font-medium text-slate-950">
                        {entry.sourceInput}
                      </strong>
                    </div>
                    <span className="shrink-0 text-[11px] text-[#648084]">
                      {formatHistoryTimestamp(entry.createdAt)}
                    </span>
                  </div>
                  <div className="grid gap-2 rounded-[1rem] border border-[#0f766e]/10 bg-white/72 px-3 py-2">
                    <span className="text-[10px] font-semibold tracking-[0.18em] text-[#5f7c80] uppercase">
                      Reusable result
                    </span>
                    <p className="m-0 line-clamp-3 text-sm leading-6 text-[#385d62]">
                      {entry.targetOutput}
                    </p>
                  </div>
                  <div className="grid gap-2 sm:flex sm:flex-wrap sm:items-center sm:justify-between">
                    <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold tracking-[0.12em] text-[#0b6d70] uppercase">
                      <span className="rounded-full border border-[#0f766e]/10 bg-white/80 px-2.5 py-1">
                        {entry.alternatives.length} highlighted ranges
                      </span>
                      {entry.diagnosticTrace?.length ? (
                        <span className="rounded-full border border-[#0f766e]/10 bg-white/80 px-2.5 py-1">
                          {entry.diagnosticTrace.length} trace steps
                        </span>
                      ) : null}
                    </div>
                    <div className="grid gap-2 sm:flex sm:flex-wrap sm:items-center">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="min-h-11 w-full rounded-full border-[#0f766e]/10 bg-white/80 text-[#0b6d70] shadow-none hover:bg-white sm:w-auto"
                        onClick={() => restoreHistoryEntry(entry)}
                      >
                        <ArrowRight className="mr-1.5 h-3.5 w-3.5" />
                        Continue
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="rounded-full text-[#0b6d70] hover:bg-white/80 hover:text-[#0b6d70]"
                        onClick={() => useResultAsNextDraft(entry.targetOutput)}
                      >
                        <FileText className="mr-1.5 h-3.5 w-3.5" />
                        Use result as draft
                      </Button>
                      {renderSavedResultBriefActions(shareKey, sharePayload)}
                      {renderHistoryDeleteActions(entry, "archive")}
                    </div>
                  </div>
                  {historyDeleteState.error && isConfirmingDelete ? (
                    <p className="m-0 text-xs font-medium text-red-700">
                      {historyDeleteState.error}
                    </p>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      </PanelCard>
    </div>
  );
}
